import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import UnfollowIcon from '../../../Assests/Unfollow.svg';
import BlockIcon from '../../../Assests/block.png';
import { unfollowUser, blockUser, reportUser } from '../../../Services/ShowsApi';
import { toggleSearchPageLoad } from '../../../redux/valueSlice';

export function useActionDialogs(userName, targetUserId, onRefresh) {
  const dispatch = useDispatch();
  const [dialogConfig, setDialogConfig] = useState(null);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [toastConfig, setToastConfig] = useState({ visible: false, message: '', showUndo: false });
  const undoClickedRef = useRef(false);

  // Extract unfollow action logic to reduce nesting
  const executeUnfollowAction = () => {
    undoClickedRef.current = false; // Reset undo state
    setToastConfig({ visible: true, message: `You've unfollowed ${userName}`, showUndo: true });
    setTimeout(() => {
      setToastConfig({ visible: false, message: '', showUndo: false });
      // Only call the API if undo was not clicked
      if (!undoClickedRef.current) {
        unfollowUser().then((response) => {
          console.log('Unfollow API response:', response);
          // Refresh the connections list after successful unfollow
          if (onRefresh && typeof onRefresh === 'function') {
            onRefresh();
          }
          // Dispatch Redux action to trigger refresh across components
          dispatch(toggleSearchPageLoad());
        }).catch((error) => {
          console.error('Unfollow API error:', error);
        });
      }
    }, 2000);
  };

  // Extract block action logic to reduce nesting
  const executeBlockAction = () => {
    undoClickedRef.current = false; // Reset undo state
    setToastConfig({ visible: true, message: `${userName} has been blocked successfully.`, showUndo: true });
    setTimeout(() => {
      setToastConfig({ visible: false, message: '', showUndo: false });
      // Only call the API if undo was not clicked
      if (!undoClickedRef.current) {
        blockUser().then((response) => {
          console.log('Block API response:', response);
          // Refresh the connections list after successful block
          if (onRefresh && typeof onRefresh === 'function') {
            onRefresh();
          }
          // Dispatch Redux action to trigger refresh across components
          dispatch(toggleSearchPageLoad());
        }).catch((error) => {
          console.error('Block API error:', error);
        });
      }
    }, 2000);
    setDialogConfig(null);
  };

  const handleUnfollow = () => {
    // Store the target user ID in localStorage for the API call
    if (targetUserId) {
      localStorage.setItem('target_user_id', targetUserId);
    }
    
    setDialogConfig({
      title: "Are You Sure You Want to Unfollow?",
      message1: `You're about to unfollow ${userName}`,
      message2: "You'll no longer see their updates or be able to collaborate directly unless you connect again.",
      actionText: "Unfollow",
      actionIcon: UnfollowIcon,
      onAction: executeUnfollowAction
    });
  };

  const handleUndo = () => {
    undoClickedRef.current = true;
    setToastConfig({ visible: false, message: '', showUndo: false });
  };

  const handleBlock = () => {
    // Store the target user ID in localStorage for the API call
    if (targetUserId) {
      localStorage.setItem('target_user_id', targetUserId);
    }
    
    setDialogConfig({
      title: "Block This User?",
      message1: `Blocking ${userName} will remove them from your connections and prevent any future messaging, collaboration, or interaction.`,
      message2: "They will not be notified that they've been blocked.",
      actionText: "Block",
      actionIcon: BlockIcon,
      onAction: executeBlockAction
    });
  };

  const handleReport = () => {
    // Store the target user ID in localStorage for the API call
    if (targetUserId) {
      localStorage.setItem('target_user_id', targetUserId);
    }
    setIsReportDialogOpen(true);
  };

  const onReport = (reportData) => {
    setIsReportDialogOpen(false);
    
    // Extract reason from reportData (can be string or object)
    let reason = '';
    console.log('Report data received:', reportData, 'Type:', typeof reportData);
    
    if (typeof reportData === 'string') {
      reason = reportData; // Direct string like "Inappropriate content"
    } else if (reportData && typeof reportData === 'object') {
      // Object structure like { reason: "Other", details: "some details" }
      reason = reportData.reason === 'Other' && reportData.details 
        ? `${reportData.reason}: ${reportData.details}` 
        : reportData.reason;
    }
    
    console.log('Extracted reason:', reason);
    
    // Show confirmation toast without undo button
    setToastConfig({ 
      visible: true, 
      message: "Thanks for reporting. We'll review this profile shortly.", 
      showUndo: false 
    });
    
    // Call the API immediately
    console.log('Calling report API with reason:', reason);
    reportUser(reason).then((response) => {
      console.log('Report API response:', response);
      // Refresh the connections list after successful report
      if (onRefresh && typeof onRefresh === 'function') {
        onRefresh();
      }
      // Dispatch Redux action to trigger refresh across components
      dispatch(toggleSearchPageLoad());
    }).catch((error) => {
      console.error('Report API error:', error);
    });
    
    // Hide the confirmation popup after 3 seconds
    setTimeout(() => {
      setToastConfig({ visible: false, message: '', showUndo: false });
    }, 3000);
  };

  return {
    dialogConfig,
    setDialogConfig,
    isReportDialogOpen,
    setIsReportDialogOpen,
    toastConfig,
    setToastConfig,
    handleUnfollow,
    handleBlock,
    handleReport,
    onReport,
    handleUndo,
  };
} 